/**
 * @page PreiskalkulatorRouten
 * @route /preiskalkulator/routen
 * @description Verwaltung der Versandrouten (Warehouse → Country → Carrier)
 *   und Stammdaten (Standorte, Länder, Carrier).
 *   3 Tabs: Routen, Standorte, Länder & Carrier.
 */
import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Globe, Plus, Trash2, Pencil, X, MapPin, Truck, Check } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

// ── Helpers ──
const apiFetch = (path, opts = {}) =>
  fetch(`${API}${path}`, { credentials: 'include', ...opts }).then(r => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });
const apiPost = (path, body) =>
  apiFetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiPut = (path, body) =>
  apiFetch(path, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiDel = (path) =>
  apiFetch(path, { method: 'DELETE' });

const TABS = [
  { key: 'routes', labelKey: 'calcRoutes.routes', Icon: Globe },
  { key: 'warehouses', labelKey: 'calcRoutes.warehouses', Icon: MapPin },
  { key: 'countriesCarriers', labelKey: 'calcRoutes.countriesCarriers', Icon: Truck },
];

// ═══════════════════════════════════════════════════════════════════════
export default function PreiskalkulatorRouten() {
  const { t } = useLanguage();
  const [tab, setTab] = useState('routes');

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-[var(--primary-light,#EBF4FD)] flex items-center justify-center">
          <Globe size={18} className="text-[var(--primary,#338edc)]" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-[var(--text-primary)]">{t('calcRoutes.title')}</h1>
          <p className="text-xs text-[var(--text-secondary)]">{t('calcRoutes.subtitle')}</p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-[2px]">
        {TABS.map(({ key, labelKey, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all flex items-center gap-1.5
              ${tab === key
                ? 'bg-[var(--bg-card,#fff)] text-[var(--text-primary)] shadow-sm'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
          >
            <Icon size={13} />
            {t(labelKey)}
          </button>
        ))}
      </div>

      {tab === 'routes' && <RoutesTab t={t} />}
      {tab === 'warehouses' && <WarehousesTab t={t} />}
      {tab === 'countriesCarriers' && <CountriesCarriersTab t={t} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  TAB 1 — Routes
// ═══════════════════════════════════════════════════════════════════════
function RoutesTab({ t }) {
  const [routes, setRoutes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [filterWarehouse, setFilterWarehouse] = useState('');
  const [filterCountry, setFilterCountry] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [rt, wh, co, ca] = await Promise.all([
      apiFetch('/api/preiskalkulator/routes'),
      apiFetch('/api/preiskalkulator/warehouses'),
      apiFetch('/api/preiskalkulator/countries'),
      apiFetch('/api/preiskalkulator/carriers'),
    ]);
    setRoutes(rt);
    setWarehouses(wh);
    setCountries(co);
    setCarriers(ca);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (route) => {
    await apiPut(`/api/preiskalkulator/routes/${route.id}`, {
      warehouse_id: route.warehouse_id,
      country_id: route.country_id,
      carrier_id: route.carrier_id,
      active: !route.active,
    });
    load();
  };

  const handleDelete = async (id) => {
    if (!confirm(t('calcRoutes.confirmDelete'))) return;
    await apiDel(`/api/preiskalkulator/routes/${id}`);
    load();
  };

  const filtered = routes.filter(r =>
    (!filterWarehouse || String(r.warehouse_id) === filterWarehouse) &&
    (!filterCountry || String(r.country_id) === filterCountry)
  );

  // Build carrier badge lookup
  const carrierMap = {};
  carriers.forEach(c => { carrierMap[c.id] = c; });

  if (loading) return <div className="text-sm text-[var(--text-secondary)]">{t('common.loading') || 'Loading...'}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <select value={filterWarehouse} onChange={e => setFilterWarehouse(e.target.value)}
            className="px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)]">
            <option value="">{t('calcRoutes.allWarehouses')}</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.flag} {w.name}</option>)}
          </select>
          <select value={filterCountry} onChange={e => setFilterCountry(e.target.value)}
            className="px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)]">
            <option value="">{t('calcRoutes.allCountries')}</option>
            {countries.map(c => <option key={c.id} value={c.id}>{c.flag} {c.name}</option>)}
          </select>
        </div>
        <button onClick={() => setModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)]">
          <Plus size={13} /> {t('calcRoutes.addRoute')}
        </button>
      </div>

      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset)]">
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.warehouse')}</th>
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.country')}</th>
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.carrier')}</th>
              <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.active')}</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => {
              const ca = carrierMap[r.carrier_id];
              return (
                <tr key={r.id} className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-hover)]">
                  <td className="px-3 py-2 text-[var(--text-primary)]">
                    <span className="mr-1.5">{warehouses.find(w => w.id === r.warehouse_id)?.flag}</span>
                    {r.warehouse_name}
                  </td>
                  <td className="px-3 py-2 text-[var(--text-primary)]">
                    <span className="mr-1.5">{countries.find(c => c.id === r.country_id)?.flag}</span>
                    {r.country_name}
                  </td>
                  <td className="px-3 py-2">
                    {ca ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold"
                        style={{ backgroundColor: ca.badge_color || '#338edc', color: ca.badge_text_color || '#fff' }}>
                        {ca.badge_text || ca.name}
                      </span>
                    ) : (
                      <span className="text-[var(--text-secondary)]">{r.carrier_name}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => toggleActive(r)}
                      className={`relative w-8 h-[18px] rounded-full transition-colors ${r.active ? 'bg-[var(--primary)]' : 'bg-[var(--text-muted)]'}`}
                    >
                      <span className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform ${r.active ? 'left-[16px]' : 'left-[2px]'}`} />
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => handleDelete(r.id)} className="p-1 rounded hover:bg-[var(--bg-hover)]">
                      <Trash2 size={13} className="text-[var(--status-error)]" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-[var(--text-tertiary)]">{t('calcRoutes.noRoutes')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <RouteModal
          t={t}
          warehouses={warehouses}
          countries={countries}
          carriers={carriers}
          onClose={() => setModal(false)}
          onSaved={() => { setModal(false); load(); }}
        />
      )}
    </div>
  );
}

function RouteModal({ t, warehouses, countries, carriers, onClose, onSaved }) {
  const [form, setForm] = useState({
    warehouse_id: warehouses[0]?.id || '',
    country_id: countries[0]?.id || '',
    carrier_id: carriers[0]?.id || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await apiPost('/api/preiskalkulator/routes', {
      warehouse_id: parseInt(form.warehouse_id),
      country_id: parseInt(form.country_id),
      carrier_id: parseInt(form.carrier_id),
      active: true,
    });
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-default,#E5E7EB)] shadow-sm w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('calcRoutes.addRoute')}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-hover)]"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <label className="block">
            <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.warehouse')}</span>
            <select value={form.warehouse_id} onChange={e => setForm(f => ({ ...f, warehouse_id: e.target.value }))}
              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)]">
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.flag} {w.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.country')}</span>
            <select value={form.country_id} onChange={e => setForm(f => ({ ...f, country_id: e.target.value }))}
              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)]">
              {countries.map(c => <option key={c.id} value={c.id}>{c.flag} {c.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.carrier')}</span>
            <select value={form.carrier_id} onChange={e => setForm(f => ({ ...f, carrier_id: e.target.value }))}
              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)]">
              {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
              {t('common.cancel') || 'Cancel'}
            </button>
            <button type="submit" disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)] disabled:opacity-50">
              {saving ? (t('common.saving') || 'Saving...') : t('calcRoutes.addRoute')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  TAB 2 — Warehouses
// ═══════════════════════════════════════════════════════════════════════
function WarehousesTab({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setItems(await apiFetch('/api/preiskalkulator/warehouses'));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!confirm(t('calcRoutes.confirmDelete'))) return;
    await apiDel(`/api/preiskalkulator/warehouses/${id}`);
    load();
  };

  if (loading) return <div className="text-sm text-[var(--text-secondary)]">{t('common.loading') || 'Loading...'}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--text-secondary)]">{t('calcRoutes.warehousesDesc')}</p>
        <button onClick={() => setModal('new')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)]">
          <Plus size={13} /> {t('calcRoutes.addWarehouse')}
        </button>
      </div>

      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset)]">
              <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium w-12">{t('calcRoutes.flag')}</th>
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.colName')}</th>
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.colCode')}</th>
              <th className="text-left px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.country')}</th>
              <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.active')}</th>
              <th className="text-center px-3 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.sort')}</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-hover)]">
                <td className="px-3 py-2 text-center text-base">{it.flag || '—'}</td>
                <td className="px-3 py-2 text-[var(--text-primary)] font-medium">{it.name}</td>
                <td className="px-3 py-2 text-[var(--text-secondary)] font-mono">{it.code}</td>
                <td className="px-3 py-2 text-[var(--text-secondary)]">{it.country || '—'}</td>
                <td className="px-3 py-2 text-center">
                  {it.active ? <Check size={14} className="inline text-green-500" /> : <X size={14} className="inline text-[var(--text-muted)]" />}
                </td>
                <td className="px-3 py-2 text-center text-[var(--text-secondary)]">{it.sort_order}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => setModal(it)} className="p-1 rounded hover:bg-[var(--bg-hover)]"><Pencil size={13} className="text-[var(--text-secondary)]" /></button>
                    <button onClick={() => handleDelete(it.id)} className="p-1 rounded hover:bg-[var(--bg-hover)]"><Trash2 size={13} className="text-[var(--status-error)]" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-[var(--text-tertiary)]">{t('calcRoutes.noData')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal !== null && (
        <WarehouseModal
          t={t}
          item={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function WarehouseModal({ t, item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item?.name || '',
    code: item?.code || '',
    country: item?.country || '',
    flag: item?.flag || '',
    sort_order: item?.sort_order ?? 0,
    active: item?.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...form, sort_order: parseInt(form.sort_order) || 0 };
    if (item) {
      await apiPut(`/api/preiskalkulator/warehouses/${item.id}`, body);
    } else {
      await apiPost('/api/preiskalkulator/warehouses', body);
    }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-default,#E5E7EB)] shadow-sm w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            {item ? t('calcRoutes.editWarehouse') : t('calcRoutes.addWarehouse')}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-hover)]"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.colName')} *</span>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.colCode')} *</span>
              <input required value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.country')}</span>
              <input value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} placeholder="e.g. Austria"
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.flag')}</span>
              <input value={form.flag} onChange={e => setForm(f => ({ ...f, flag: e.target.value }))} placeholder="e.g. AT"
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.sort')}</span>
              <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="flex items-end gap-2 pb-1.5">
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="rounded" />
              <span className="text-xs text-[var(--text-secondary)]">{t('calcRoutes.active')}</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
              {t('common.cancel') || 'Cancel'}
            </button>
            <button type="submit" disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)] disabled:opacity-50">
              {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  TAB 3 — Countries & Carriers (Split-View)
// ═══════════════════════════════════════════════════════════════════════
function CountriesCarriersTab({ t }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <CountriesPanel t={t} />
      <CarriersPanel t={t} />
    </div>
  );
}

// ── Countries (left) ──
function CountriesPanel({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setItems(await apiFetch('/api/preiskalkulator/countries'));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!confirm(t('calcRoutes.confirmDelete'))) return;
    await apiDel(`/api/preiskalkulator/countries/${id}`);
    load();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('calcRoutes.countries')}</h3>
        <button onClick={() => setModal('new')}
          className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)]">
          <Plus size={12} /> {t('calcRoutes.addCountry')}
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-[var(--text-secondary)]">{t('common.loading') || 'Loading...'}</div>
      ) : (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset)]">
                <th className="text-center px-2 py-2 text-[var(--text-secondary)] font-medium w-10">{t('calcRoutes.flag')}</th>
                <th className="text-left px-2 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.colName')}</th>
                <th className="text-left px-2 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.colCode')}</th>
                <th className="text-left px-2 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.region')}</th>
                <th className="text-center px-2 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.active')}</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-hover)]">
                  <td className="px-2 py-1.5 text-center text-base">{it.flag || '—'}</td>
                  <td className="px-2 py-1.5 text-[var(--text-primary)] font-medium">{it.name}</td>
                  <td className="px-2 py-1.5 text-[var(--text-secondary)] font-mono">{it.code}</td>
                  <td className="px-2 py-1.5 text-[var(--text-secondary)]">{it.region || '—'}</td>
                  <td className="px-2 py-1.5 text-center">
                    {it.active ? <Check size={13} className="inline text-green-500" /> : <X size={13} className="inline text-[var(--text-muted)]" />}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex gap-0.5 justify-end">
                      <button onClick={() => setModal(it)} className="p-1 rounded hover:bg-[var(--bg-hover)]"><Pencil size={12} className="text-[var(--text-secondary)]" /></button>
                      <button onClick={() => handleDelete(it.id)} className="p-1 rounded hover:bg-[var(--bg-hover)]"><Trash2 size={12} className="text-[var(--status-error)]" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-4 text-center text-[var(--text-tertiary)]">{t('calcRoutes.noData')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal !== null && (
        <CountryModal
          t={t}
          item={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function CountryModal({ t, item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item?.name || '',
    code: item?.code || '',
    flag: item?.flag || '',
    region: item?.region || '',
    sort_order: item?.sort_order ?? 0,
    active: item?.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...form, sort_order: parseInt(form.sort_order) || 0 };
    if (item) {
      await apiPut(`/api/preiskalkulator/countries/${item.id}`, body);
    } else {
      await apiPost('/api/preiskalkulator/countries', body);
    }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-default,#E5E7EB)] shadow-sm w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            {item ? t('calcRoutes.editCountry') : t('calcRoutes.addCountry')}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-hover)]"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.colName')} *</span>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.colCode')} *</span>
              <input required value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} placeholder="e.g. AT"
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.flag')}</span>
              <input value={form.flag} onChange={e => setForm(f => ({ ...f, flag: e.target.value }))} placeholder="AT"
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.region')}</span>
              <input value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))} placeholder="e.g. EU"
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="flex items-end gap-2 pb-1.5">
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="rounded" />
              <span className="text-xs text-[var(--text-secondary)]">{t('calcRoutes.active')}</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
              {t('common.cancel') || 'Cancel'}
            </button>
            <button type="submit" disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)] disabled:opacity-50">
              {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Carriers (right) ──
function CarriersPanel({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setItems(await apiFetch('/api/preiskalkulator/carriers'));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!confirm(t('calcRoutes.confirmDelete'))) return;
    await apiDel(`/api/preiskalkulator/carriers/${id}`);
    load();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('calcRoutes.carriers')}</h3>
        <button onClick={() => setModal('new')}
          className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)]">
          <Plus size={12} /> {t('calcRoutes.addCarrier')}
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-[var(--text-secondary)]">{t('common.loading') || 'Loading...'}</div>
      ) : (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset)]">
                <th className="text-left px-2 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.badge')}</th>
                <th className="text-left px-2 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.colName')}</th>
                <th className="text-left px-2 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.colCode')}</th>
                <th className="text-center px-2 py-2 text-[var(--text-secondary)] font-medium">{t('calcRoutes.active')}</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className="border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-hover)]">
                  <td className="px-2 py-1.5">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold"
                      style={{ backgroundColor: it.badge_color || '#338edc', color: it.badge_text_color || '#fff' }}>
                      {it.badge_text || it.name}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-[var(--text-primary)] font-medium">{it.name}</td>
                  <td className="px-2 py-1.5 text-[var(--text-secondary)] font-mono">{it.code}</td>
                  <td className="px-2 py-1.5 text-center">
                    {it.active ? <Check size={13} className="inline text-green-500" /> : <X size={13} className="inline text-[var(--text-muted)]" />}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex gap-0.5 justify-end">
                      <button onClick={() => setModal(it)} className="p-1 rounded hover:bg-[var(--bg-hover)]"><Pencil size={12} className="text-[var(--text-secondary)]" /></button>
                      <button onClick={() => handleDelete(it.id)} className="p-1 rounded hover:bg-[var(--bg-hover)]"><Trash2 size={12} className="text-[var(--status-error)]" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-[var(--text-tertiary)]">{t('calcRoutes.noData')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal !== null && (
        <CarrierModal
          t={t}
          item={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function CarrierModal({ t, item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item?.name || '',
    code: item?.code || '',
    badge_text: item?.badge_text || '',
    badge_color: item?.badge_color || '#338edc',
    badge_text_color: item?.badge_text_color || '#ffffff',
    sort_order: item?.sort_order ?? 0,
    active: item?.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...form, sort_order: parseInt(form.sort_order) || 0 };
    if (item) {
      await apiPut(`/api/preiskalkulator/carriers/${item.id}`, body);
    } else {
      await apiPost('/api/preiskalkulator/carriers', body);
    }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-default,#E5E7EB)] shadow-sm w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            {item ? t('calcRoutes.editCarrier') : t('calcRoutes.addCarrier')}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-hover)]"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.colName')} *</span>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.colCode')} *</span>
              <input required value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.badgeText')}</span>
            <input value={form.badge_text} onChange={e => setForm(f => ({ ...f, badge_text: e.target.value }))} placeholder={form.name || 'Badge label'}
              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.badgeColor')}</span>
              <div className="flex items-center gap-2">
                <input type="color" value={form.badge_color} onChange={e => setForm(f => ({ ...f, badge_color: e.target.value }))}
                  className="w-8 h-8 rounded border border-[var(--border-input)] cursor-pointer" />
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold"
                  style={{ backgroundColor: form.badge_color, color: form.badge_text_color }}>
                  {form.badge_text || form.name || 'Preview'}
                </span>
              </div>
            </label>
            <label className="block">
              <span className="text-xs text-[var(--text-secondary)] mb-1 block">{t('calcRoutes.badgeTextColor')}</span>
              <input type="color" value={form.badge_text_color} onChange={e => setForm(f => ({ ...f, badge_text_color: e.target.value }))}
                className="w-8 h-8 rounded border border-[var(--border-input)] cursor-pointer" />
            </label>
            <label className="flex items-end gap-2 pb-1.5">
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="rounded" />
              <span className="text-xs text-[var(--text-secondary)]">{t('calcRoutes.active')}</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
              {t('common.cancel') || 'Cancel'}
            </button>
            <button type="submit" disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-white bg-[var(--primary)] rounded-lg hover:bg-[var(--primary-hover)] disabled:opacity-50">
              {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
